import { useRouter } from 'vue-router';
import { Logger } from '@/common/Logger';
import { QueryParamName } from '@/common/constants/items';
import { useSearchStore } from '@/stores/search';

export function useLocationSearch() {
  const searchStore = useSearchStore();
  const router = useRouter();

  function handleSubmitLocationSearch(searchData: {
    text: string;
    coordinates: null;
  }) {
    if (searchStore.locationSearchText === searchData.text) {
      searchStore.locationSearchChanged = false;
      return;
    }

    searchStore.locationSearchText = searchData.text;

    // Reset any existing coordinate values
    searchStore.ne_lat = null;
    searchStore.ne_lng = null;
    searchStore.sw_lat = null;
    searchStore.sw_lng = null;

    // Set the location search changed flag to trigger a search
    searchStore.locationSearchChanged = true;
    // Reset the coordinates changed flag since we're doing a text-only search
    searchStore.locationSearchCoordinatesChanged = false;
  }

  function handleSubmitGoogleLocationSearch(searchData: {
    text: string;
    coordinates: {
      ne_lat: number;
      ne_lng: number;
      sw_lat: number;
      sw_lng: number;
    };
  }) {
    const { text, coordinates } = searchData;

    // Check if both text and coordinates match what's already in the store
    if (
      searchStore.locationSearchText === text &&
      searchStore.ne_lat === coordinates.ne_lat &&
      searchStore.ne_lng === coordinates.ne_lng &&
      searchStore.sw_lat === coordinates.sw_lat &&
      searchStore.sw_lng === coordinates.sw_lng
    ) {
      searchStore.locationSearchChanged = false;
      searchStore.locationSearchCoordinatesChanged = false;
      return;
    }

    searchStore.locationSearchText = text;

    // Update coordinate values
    searchStore.ne_lat = coordinates.ne_lat;
    searchStore.ne_lng = coordinates.ne_lng;
    searchStore.sw_lat = coordinates.sw_lat;
    searchStore.sw_lng = coordinates.sw_lng;

    // Important: This is the flag that triggers the search process
    searchStore.locationSearchCoordinatesChanged = true;
  }

  function handleClearLocationSearch() {
    Logger.debug('[useLocationSearch] Clearing location search');
    searchStore.locationSearchText = '';

    // Reset all coordinate values
    searchStore.ne_lat = null;
    searchStore.ne_lng = null;
    searchStore.sw_lat = null;
    searchStore.sw_lng = null;

    // Set the location search changed flag to trigger a search
    searchStore.locationSearchChanged = true;
    searchStore.locationSearchCoordinatesChanged = true;
  }

  function navigateToItemList(searchData: {
    text: string;
    coordinates: null | {
      ne_lat: number;
      ne_lng: number;
      sw_lat: number;
      sw_lng: number;
    };
  }) {
    const query: any = {
      [QueryParamName.LOC_SEARCH]: searchData.text,
    };

    // Add coordinates to query if they exist
    if (searchData.coordinates) {
      query.ne_lat = searchData.coordinates.ne_lat;
      query.ne_lng = searchData.coordinates.ne_lng;
      query.sw_lat = searchData.coordinates.sw_lat;
      query.sw_lng = searchData.coordinates.sw_lng;
    }

    router.push({
      name: 'item-list',
      query,
    });
  }

  return {
    handleSubmitLocationSearch,
    handleSubmitGoogleLocationSearch,
    handleClearLocationSearch,
    navigateToItemList,
  };
}
